/* eslint-disable react/jsx-props-no-spreading */

import { HTMLAttributes, ReactNode } from 'preact/compat';
import { useDispatch } from 'react-redux';

import { isEmbedded, loadPage } from '@/helpers';
import { cn } from '@/lib/utils';
import { useSelector, useStore } from '@/redux/helper';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';

import { setProductsPromptModalState } from '../products/ProductsPromptModal/redux/actions';
import { createSelectProductsPromptModalState } from '../products/ProductsPromptModal/redux/selectors';
import { buildHabitatURL } from './utils';

export interface HabitatLinkProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  habitatId: string;
  slug: string;
  zooSlug: string;
  query?: string;
  productsPromptModalState?: Parameters<typeof setProductsPromptModalState>[0];
  children: ReactNode | ((props: { restricted: boolean }) => ReactNode);
}

type RestrictedHabitatLinkProps = Omit<HabitatLinkProps, 'children'> & {
  children: ReactNode;
};
type AccessibleHabitatLinkProps = Omit<RestrictedHabitatLinkProps, 'habitatId' | 'productsPromptModalState'>;

const AccessibleHabitatLink = ({
  slug,
  zooSlug,
  query,
  children,
  className,
  onClick,
  ...rest
}: AccessibleHabitatLinkProps) => (
  <a
    href={buildHabitatURL(zooSlug, slug, query)}
    data-native={isEmbedded() || undefined}
    className={cn('preflight preflight-a', className)}
    {...rest}
    onClick={(e) => {
      onClick?.(e);
      if (e.defaultPrevented) {
        return;
      }
      if (isEmbedded()) {
        e.preventDefault();
        e.stopPropagation();
        loadPage(buildHabitatURL(zooSlug, slug, query));
      }
    }}
  >
    {children}
  </a>
);

const RestrictedHabitatLink = ({
  habitatId,
  slug,
  zooSlug,
  query,
  children,
  onClick,
  productsPromptModalState,
  ...rest
}: RestrictedHabitatLinkProps) => {
  const dispatch = useDispatch();
  const store = useStore();
  const logged = useSelector((state) => state.user.logged);
  return (
    <AccessibleHabitatLink
      slug={slug}
      zooSlug={zooSlug}
      query={query}
      {...rest}
      data-native={logged || isEmbedded() || undefined}
      onClick={(e) => {
        if (isEmbedded()) {
          onClick?.(e);
          return;
        }
        if (logged) {
          e.preventDefault();
          const defaultState = createSelectProductsPromptModalState(habitatId)(store.getState());
          const state: typeof productsPromptModalState = {
            ...defaultState,
            ...productsPromptModalState,
            entryPoint: 'habitat-link',
          };
          dispatch(setProductsPromptModalState(state));
        }
        onClick?.(e);
      }}
    >
      {children}
    </AccessibleHabitatLink>
  );
};

const HabitatLink = ({ habitatId, children, productsPromptModalState, ...rest }: HabitatLinkProps) => {
  const canAccessHabitat = useSelector(createSelectCanAccessHabitat(habitatId));
  return canAccessHabitat ? (
    <AccessibleHabitatLink {...rest}>
      {typeof children === 'function' ? children({ restricted: false }) : children}
    </AccessibleHabitatLink>
  ) : (
    <RestrictedHabitatLink habitatId={habitatId} productsPromptModalState={productsPromptModalState} {...rest}>
      {typeof children === 'function' ? children({ restricted: true }) : children}
    </RestrictedHabitatLink>
  );
};

export default HabitatLink;
